import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEOMeta from './SEOMeta';
import { Button } from './ui/button';
import PublicNavbar from './PublicNavbar';
import {
  ArrowRight,
  Heart,
  Target,
  Lightbulb,
  Users,
  Home,
  Brain,
  Bot,
  Sparkles,
  CheckCircle,
  Clock,
  Shield,
  TrendingUp
} from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Heart,
      title: 'Landlord-First Design',
      description: 'Every feature is built with real landlord needs in mind. We use our own product to manage our properties.'
    },
    {
      icon: Target,
      title: 'Practical AI',
      description: 'We believe AI should solve real problems, not create new ones. Our agents are designed to be helpful, not hype.'
    },
    {
      icon: Lightbulb,
      title: 'Continuous Innovation',
      description: 'Property management is evolving. We\'re committed to staying ahead with cutting-edge technology that actually works.'
    },
    {
      icon: Shield,
      title: 'Trust & Transparency',
      description: 'Your data is your data. We\'re building enterprise-grade security because your tenants\' information matters.'
    }
  ];

  const timeline = [
    {
      year: '2004',
      title: 'Our Journey Begins',
      description: 'Started managing our first rental property in Central Ohio. Learned the hard way what works and what doesn\'t.'
    },
    {
      year: '2010',
      title: 'Portfolio Growth',
      description: 'Expanded to multiple properties. Realized traditional tools weren\'t keeping up with our needs.'
    },
    {
      year: '2018',
      title: 'The AI Spark',
      description: 'Began experimenting with automation and AI to handle routine tenant communications.'
    },
    {
      year: '2023',
      title: 'NivasaPro.ai Founded',
      description: 'Launched NivasaPro.ai to bring our 20 years of experience together with modern AI technology.'
    },
    {
      year: '2024',
      title: 'Agentic AI Platform',
      description: 'Developed our breakthrough agentic AI system that can autonomously handle complex property management tasks.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOMeta
        title="About: Built for Independent Landlords in Central Ohio"
        description="Learn how NivasaPro was built to give independent landlords and property managers the same AI-powered tools that large property management companies use."
        canonical="/about"
      />
      {/* Navigation */}
      <PublicNavbar activeRoute="/about" />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-wide text-cyan-700 uppercase mb-4">
            Our Story
          </p>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900 mb-6">
            Built by Landlords,{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Powered by AI
            </span>
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            After 20 years of managing rental properties in Central Ohio, we built the
            AI-powered platform we always wished we had. NivasaPro.ai is where decades
            of real-world experience meets cutting-edge technology.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-white border-y">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  From Property Owners to PropTech Pioneers
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    We didn't start as a tech company. We started as landlords, juggling
                    late-night maintenance calls, chasing down rent payments, and spending
                    countless hours on tasks that felt like they should be simpler.
                  </p>
                  <p>
                    After two decades of managing properties across Central Ohio, we'd seen
                    every problem imaginable. Burst pipes at 2 AM. Tenants who couldn't reach
                    us when they needed help. Vendors who didn't show up. The endless paperwork.
                  </p>
                  <p>
                    When AI technology began to mature, we saw an opportunity: what if we could
                    build an intelligent assistant that understood property management the way
                    we do? Not just automation, but true <strong>agentic AI</strong> that could
                    think, prioritize, and act on behalf of landlords.
                  </p>
                  <p>
                    That's why we built NivasaPro.ai, a platform born from real experience,
                    designed to solve the problems we lived every day.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl blur-2xl opacity-20"></div>
                <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                      <Home className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">20+</div>
                      <div className="text-gray-600">Years of Experience</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">Active property owners</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">Central Ohio focused</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">Using our own product daily</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Agentic AI */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-wide text-purple-700 uppercase mb-3">
                The Technology
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What is Agentic AI?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                It's not just chatbots or simple automation. It's a new paradigm in artificial intelligence
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Bot className="h-5 w-5 text-gray-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-500">Traditional AI</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    Responds to specific commands
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    Limited to predefined workflows
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    Requires constant human oversight
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    Can't handle unexpected situations
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Brain className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Agentic AI</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    Understands context and intent
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    Makes autonomous decisions
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    Coordinates complex multi-step tasks
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    Adapts to new situations intelligently
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">How Our Agents Work</h3>
                  <p className="text-gray-600 mb-4">
                    When a tenant reports a maintenance issue, our AI agent doesn't just log a ticket.
                    It analyzes the problem, determines urgency, checks your vendor availability,
                    schedules the repair, notifies all parties, and follows up to ensure completion, all autonomously.
                  </p>
                  <p className="text-gray-600">
                    You stay informed and in control, but the AI handles the coordination that used to eat up your time.
                    It's like having a dedicated property manager who works 24/7 and never forgets a detail.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Journey
              </h2>
              <p className="text-xl text-gray-600">
                Two decades of property management experience, distilled into AI
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500 to-blue-600"></div>

              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Timeline dot */}
                    <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-white border-4 border-cyan-500 rounded-full transform -translate-x-1/2"></div>

                    {/* Content */}
                    <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                        <div className={`inline-block bg-cyan-100 text-cyan-800 text-sm font-bold px-3 py-1 rounded-full mb-2`}>
                          {item.year}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Believe
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we build
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all text-center"
                >
                  <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-7 w-7 text-cyan-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Central Ohio */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-semibold tracking-wide text-orange-700 uppercase mb-3">
                  Local Roots
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Why We Focus on Central Ohio
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    We could have built a generic platform for landlords everywhere.
                    Instead, we chose to start where we know best: Central Ohio.
                  </p>
                  <p>
                    By focusing on one market, we can build deep integrations with local
                    vendors, understand regional regulations, and provide support that
                    actually knows your neighborhood.
                  </p>
                  <p>
                    Our AI is trained on Central Ohio specific data: local maintenance costs,
                    seasonal patterns, rental market trends, and compliance requirements.
                    This isn't one-size-fits-all; it's built for you.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-5 border border-orange-200 flex items-center space-x-4">
                  <Clock className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Local Response Times</div>
                    <div className="text-sm text-gray-600">Vendors who know the area</div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-orange-200 flex items-center space-x-4">
                  <Shield className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Ohio Compliance</div>
                    <div className="text-sm text-gray-600">Built-in regulatory knowledge</div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-orange-200 flex items-center space-x-4">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Market Intelligence</div>
                    <div className="text-sm text-gray-600">Local rent and trend data</div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-orange-200 flex items-center space-x-4">
                  <Users className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Community First</div>
                    <div className="text-sm text-gray-600">Growing with local landlords</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-600 to-blue-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Us on This Journey
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            We're just getting started. Be part of the future of property management
            in Central Ohio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-cyan-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
              onClick={() => navigate('/')}
            >
              Get Early Access <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold"
              onClick={() => navigate('/features')}
            >
              Explore Features
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
                <span className="text-xl font-bold text-white">NivasaPro.ai</span>
              </div>
              <p className="text-sm text-gray-400">
                Agentic rental property management powered by AI.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-cyan-400 transition text-left">Features</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Pricing</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Security</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-cyan-400 transition text-left">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-cyan-400 transition text-left">Blog</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Careers</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-cyan-400 transition text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-cyan-400 transition text-left">Terms of Service</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Contact</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 NivasaPro.ai. All rights reserved. Built for Central Ohio landlords.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
